// ── 흐름 설정 — 매달 고정 지출 입력 칸 (2026-10-13) ──────────────────────────
// 정해진 기간 동안 매달 '나가는 돈'에 자동으로 더할 금액(예: 대표 가수금 상환 매달 1,000만원).
//   사용자 결정: 급여와 따로 한 줄로 보이고, 급여가 아니라 1.1 을 곱하지 않는다.
// 금액 = type="text" + inputMode="numeric"(화살표 금지 규칙). 달 = <input type="month"> 값('YYYY-MM') 문자열 그대로 보낸다.
// 저장은 FloorSettingsModal 이 한다 — 여기는 줄 고치기·검사·보낼 모양 만들기만.

import React from 'react';
import { fmtAmount } from './constants';

export const FIXED_MAX_ROWS = 20; // 서버 FIXED_OUTFLOW_MAX_ROWS 와 같은 값
const LABEL_MAX = 40;

const onlyDigits = (v) => String(v).replace(/[^0-9]/g, '');
let keySeq = 0;

/** 새 줄 — 시작 달 기본값은 이번 달 */
export function newFixedRow(defaultYm) {
  keySeq += 1;
  return { key: `new-${keySeq}`, id: null, label: '', amount: '', startYm: defaultYm || '', endYm: '' };
}

/** 서버 줄 → 화면 줄 */
export function toEditRows(rows) {
  return (Array.isArray(rows) ? rows : []).map((r) => ({
    key: `id-${r.id}`, id: r.id, label: r.label || '', amount: String(r.amount ?? ''), startYm: r.startYm || '', endYm: r.endYm || '',
  }));
}

/** 화면 줄 → 서버로 보낼 모양 */
export function toPayloadRows(rows) {
  return rows.map((r) => ({
    id: r.id ?? null, label: r.label.trim(), amount: Number(r.amount), startYm: r.startYm, endYm: r.endYm || null,
  }));
}

/** 저장 전 검사 — 문제가 있으면 알림 문구, 없으면 null */
export function fixedRowsError(rows) {
  for (let i = 0; i < rows.length; i += 1) {
    const r = rows[i];
    const at = `매달 고정 지출 ${i + 1}번째 줄`;
    if (!r.label.trim()) return `${at} 이름을 입력해 주세요.`;
    if (!r.amount || Number(r.amount) <= 0) return `${at} 금액을 입력해 주세요.`;
    if (!r.startYm) return `${at} 시작 달을 골라 주세요.`;
    if (r.endYm && r.endYm < r.startYm) return `${at} 끝 달이 시작 달보다 앞입니다.`;
  }
  return null;
}

export default function FixedOutflowSettings({ rows, onChange, disabled, defaultYm, loadFailed }) {
  const update = (key, patch) => onChange(rows.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  const remove = (key) => onChange(rows.filter((r) => r.key !== key));
  const add = () => onChange([...rows, newFixedRow(defaultYm)]);

  return (
    <section>
      <h4 style={sectionTitle}>매달 고정 지출</h4>
      <div style={introStyle}>
        적어 둔 기간 동안 <b>매달 나가는 돈</b>에 자동으로 더합니다. 급여처럼 그 달 말일에 나가는 것으로 봅니다.
        급여가 아니라서 1.1을 곱하지 않습니다.
        금액이 바뀌면 지금 줄에 <b>끝 달</b>을 적고 새 줄을 더하세요. 그래야 지난달 숫자가 그대로 남습니다.
      </div>
      {loadFailed ? (
        <div style={errorStyle}>목록을 불러오지 못해 지금은 고칠 수 없습니다. 창을 닫고 다시 열어 주세요.</div>
      ) : (
        <>
          {rows.length === 0 && <div style={emptyStyle}>아직 넣은 것이 없습니다.</div>}
          {rows.map((r, i) => (
            <div key={r.key} style={rowCard} data-fixed-row={i + 1}>
              <div style={line}>
                <input
                  aria-label={`${i + 1}번째 줄 이름`}
                  placeholder="이름 (예: 정명철 가수금 상환)"
                  value={r.label}
                  maxLength={LABEL_MAX}
                  onChange={(e) => update(r.key, { label: e.target.value })}
                  style={textInput}
                  disabled={disabled}
                />
                <button type="button" onClick={() => remove(r.key)} style={removeBtn} disabled={disabled}
                  aria-label={`${i + 1}번째 줄 지우기`}>
                  지우기
                </button>
              </div>
              <div style={line}>
                <input
                  type="text"
                  inputMode="numeric"
                  aria-label={`${i + 1}번째 줄 금액`}
                  placeholder="매달 금액"
                  value={r.amount}
                  onChange={(e) => update(r.key, { amount: onlyDigits(e.target.value) })}
                  style={amountInput}
                  disabled={disabled}
                />
                <span style={unitStyle}>원</span>
              </div>
              <div style={hintStyle}>{r.amount === '' ? ' ' : `${fmtAmount(r.amount)}원`}</div>
              <div style={monthLine}>
                <label style={monthLabel}>
                  시작 달
                  <input type="month" aria-label={`${i + 1}번째 줄 시작 달`} value={r.startYm}
                    onChange={(e) => update(r.key, { startYm: e.target.value })} style={monthInput} disabled={disabled} />
                </label>
                <label style={monthLabel}>
                  끝 달
                  <input type="month" aria-label={`${i + 1}번째 줄 끝 달`} value={r.endYm}
                    onChange={(e) => update(r.key, { endYm: e.target.value })} style={monthInput} disabled={disabled} />
                </label>
              </div>
              <div style={hintLeft}>끝 달을 비우면 계속 더합니다.</div>
            </div>
          ))}
          <button type="button" onClick={add} style={addBtn} disabled={disabled || rows.length >= FIXED_MAX_ROWS}>
            + 줄 추가
          </button>
        </>
      )}
    </section>
  );
}

const sectionTitle = { margin: '0 0 6px', fontSize: 14, fontWeight: 800, color: 'var(--text)' };
const introStyle = { fontSize: 13, color: 'var(--text-sub)', lineHeight: 1.6, marginBottom: 12 };
const errorStyle = { fontSize: 13, color: '#dc2626', fontWeight: 600, padding: '8px 0' };
const emptyStyle = { fontSize: 13, color: 'var(--text-muted)', padding: '4px 0 10px' };
const rowCard = { border: '1px solid var(--border)', borderRadius: 8, padding: 10, marginBottom: 10, background: 'var(--bg-subtle)' };
const line = { display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 };
const inputBase = { minWidth: 0, padding: '8px 10px', border: '1px solid var(--border)', borderRadius: 6, fontSize: 14, background: 'var(--bg-card)', color: 'var(--text)', boxSizing: 'border-box' };
const textInput = { ...inputBase, flex: 1 };
const amountInput = { ...inputBase, flex: 1, textAlign: 'right', fontVariantNumeric: 'tabular-nums' };
const unitStyle = { fontSize: 13, color: 'var(--text-sub)', flexShrink: 0 };
const hintStyle = { fontSize: 12, color: 'var(--text-muted)', textAlign: 'right', marginTop: -2, marginBottom: 6, minHeight: 16 };
const hintLeft = { fontSize: 12, color: 'var(--text-muted)', marginTop: 2 };
const monthLine = { display: 'flex', gap: 8, flexWrap: 'wrap' };
const monthLabel = { display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, fontWeight: 700, color: 'var(--text-sub)', flex: '1 1 140px', minWidth: 0 };
const monthInput = { ...inputBase, width: '100%', fontSize: 13 };
const removeBtn = { height: 36, padding: '0 12px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-card)', color: '#dc2626', fontSize: 13, fontWeight: 700, cursor: 'pointer', flexShrink: 0 };
const addBtn = { width: '100%', height: 38, borderRadius: 8, border: '1px dashed var(--border)', background: 'var(--bg-card)', color: 'var(--text)', fontSize: 13, fontWeight: 700, cursor: 'pointer' };
